import Translation from 'components/Translation'
import { IError, useAppContext } from 'context/AppContext'

import { AuthContext, useAuth } from 'context/AuthContext'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { classNames } from 'utils/dom-helpers'

import { createModal } from '../ModalFactory'
import { ModalWrapper } from '../ModalWrapper'
import { RecurrenceSchedule } from '../types'
import ClassItem, { days_of_week } from './ClassItem'
import { DEFAULT_ERROR_STATE } from '../../../context/AppContext'
//@ts-ignore
import { v4 as uuidv4 } from 'uuid'
import { hasTimeOverlap, isSameDay, toHHMM, toYmd } from 'utils/string-localization-helper'
import { XIcon } from '@heroicons/react/solid'
import { postApiRequest } from 'utils/fetch-helper'
import GenericError from 'components/GenericError/GenericError'
import { useTenantContext } from 'context/TenantContext'
import { ServiceApiTypeEnum } from 'types/service'
import { GlobalDialogType } from '../GlobalDialog'
export interface IClassOption {
    name: string
    id: string
    duration?: number | string
    slots?: any
}
export interface IClass { //TODO: refactor this
    id: string
    service: IClassOption
    location: IClassOption
    locationSpace: any
    instructor: IClassOption
    isRecurring: boolean
    endDate: any
    repeats?: string
    date: Date
    time: any
    day: any
}

const ModalProvider = createModal(
    AuthContext,
    'ClassModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Create</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-chevron-left " />
        </span>
    )
)

export const ClassModalOpener = ModalProvider.Opener
export const ClassModalCloser = ModalProvider.Closer

const initialClass: IClass[] = [
    {
        id: uuidv4(),
        service: { id: '', name: '' },
        location: { id: '', name: '' },
        locationSpace: '',
        instructor: { id: '', name: '' },
        isRecurring: false,
        endDate: '',
        date: new Date(),
        time: toHHMM(new Date()),
        day: days_of_week[new Date().getDay()],
        repeats: RecurrenceSchedule.WEEKLY,
    },
]

function ClassModal() {
    const { ClassModal, GlobalDialog } = useAuth()
    const { locations, services, staff, toggleForceRecacheCalendar } =
        useTenantContext()
    const { translations } = useAppContext()
    const [classList, setClassList] = useState<IClass[]>(initialClass)

    const [error, setError] = useState<IError>(DEFAULT_ERROR_STATE)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        handleInit()
    }, [locations, services, staff])

    const getServices = () =>
        services?.filter(
            (item) =>
                item.service_type !== ServiceApiTypeEnum.APPOINTMENT &&
                item.service_type !== ServiceApiTypeEnum.SERIES
        ) as unknown as IClassOption[]

    const handleInit = () => {
        if (services?.length && locations?.length && staff?.length) {
            const service = getServices()[0]
            const location = locations[0]
            const staffValues = {
                id: staff[0].id,
                name: `${staff[0]?.user?.first_name} ${staff[0]?.user?.last_name}`,
            }

            setClassList((prev: any) => {
                const prevState = [...prev]
                return prevState.map((item) => ({
                    ...item,
                    service,
                    location,
                    instructor: staffValues,
                }))
            })
        }
    }

    const addNewClass = () => {
        if (classList.length < 5) {
            const service = getServices()[0]
            const location = locations[0]
            const staffValues = staff[0]
            const newClass: IClass = {
                id: uuidv4(),
                service,
                instructor: staffValues,
                location,
                locationSpace: '',
                endDate: '',
                repeats: RecurrenceSchedule.WEEKLY,
                isRecurring: false,
                date: new Date(),
                time: toHHMM(new Date()),
                day: days_of_week[new Date().getDay()],
            }
            setClassList((prev) => [...prev, newClass])
        }
    }

    const removeClass = (index: number, id: string) => {
        setClassList((prev) => prev.filter((_, idx) => idx !== index))
    }

    const getHeader = () => {
        return (
            <div
                className="flex px-4 text-gray-500 z-10 w-full pt-4 bg-opacity-30 pb-3"
                style={{
                    backgroundColor: 'rgba(207, 226, 245, 0.2)',
                    height: '81px',
                }}
            >
                <div className="flex items-center ">
                    <div>
                        <h1
                            className="text-2xl font-medium"
                            style={{ color: '#23476B' }}
                        >
                            <Translation content_key="offer_class" />
                        </h1>
                    </div>
                    <span
                        className="text-sm inline-block w-5 h-5  text-center font-extralight absolute right-0  mr-3 cursor-pointer"
                        onClick={() => handleModalClose()}
                        style={{ color: 'rgb(35, 71, 107)' }}
                    >
                        <XIcon />
                    </span>
                </div>
            </div>
        )
    }

    const transformRequest = useCallback(() => {
        const data = classList.map((item) => {
            const structure = {
                duration: item.service?.duration,
                effectiveDate: toYmd(item.date),
                groupClassSetting: {
                    locationId: item.location.id,
                    locationSpaceId: item?.locationSpace?.value as string,
                    staffId: item.instructor.id,
                },
                recurring: item.isRecurring,
                serviceId: item.service.id,
                startTime: item.time,
            }

            const final: any = item.isRecurring
                ? item.endDate
                    ? {
                        ...structure,
                        dayOfWeek: item.day,
                        recurrenceSchedule: item.repeats,
                        expiryDate: toYmd(item.endDate),
                    }
                    : {
                        ...structure,
                        dayOfWeek: item.day,
                        recurrenceSchedule: item.repeats,
                    }
                : structure

            if (
                item.repeats === RecurrenceSchedule.DAILY ||
                item.repeats === RecurrenceSchedule.MONTHLY_ON_DAY ||
                item.repeats === RecurrenceSchedule.MONTHLY_ON_DATE
            ) {
                delete final.dayOfWeek
            }

            return final
        })

        return data
    }, [classList])

    const onSubmit = async () => {
        try {
            setIsLoading(true)
            const requestBody = transformRequest()
            const responses = requestBody.map((requestItem) =>
                postApiRequest('/v1/group_classes', requestItem)
            )

            let result = await Promise.all(responses)

            let resolve = result.every((item) => item.ok === true)

            if (!resolve) {
                const err = result.filter((item) => !item.ok)
                const def: IError = {
                    message: 'Error while creating classes!',
                    tag: '',
                }
                throw err.length > 0
                    ? { message: err[0].message as string, tag: '' }
                    : def
            } else {
                GlobalDialog.setAttributes({
                    title: 'common.success_grp_class',
                    button: 'common.back_btn',
                    className: 'w-[32rem]',
                    type: GlobalDialogType.SUCCESS,
                })
                GlobalDialog.open()
                handleModalClose(true)
            }
        } catch (err) {
            setError(err)
        }
        setIsLoading(false)
    }

    const handleModalClose = (force_recache?: boolean) => {
        setClassList(initialClass)
        handleInit()
        if (force_recache) {
            toggleForceRecacheCalendar(true)
        }
        ClassModal.close()
    }

    function hasOverlap(): boolean {
        let has_overlap = false
        if (classList.length > 1) {
            classList.forEach((class_item, index) => {
                for (let itr = index + 1; itr < classList.length; itr++) {
                    if (class_item.instructor.id == classList[itr].instructor.id
                        && isSameDay(class_item.date, classList[itr].date)) {
                        let a_start_time: number[] = (class_item.time as string).split(":").map(Number)
                        let a_end_time: number[] = structuredClone(a_start_time)
                        let b_start_time: number[] = (classList[itr].time as string).split(":").map(Number)
                        let b_end_time: number[] = structuredClone(b_start_time)

                        a_end_time[1] = a_start_time[1] + (class_item.service.duration as number)
                        if (a_end_time[1] >= 60) {
                            a_end_time[0] += 1
                            a_end_time[1] -= 60
                        }

                        b_end_time[1] = b_end_time[1] + (class_item.service.duration as number)
                        if (b_end_time[1] >= 60) {
                            b_end_time[0] += 1
                            b_end_time[1] -= 60
                        }
                        if (hasTimeOverlap(a_start_time, a_end_time, b_start_time, b_end_time)) {
                            has_overlap = true
                            return
                        }
                    }
                }
            })
        }
        return has_overlap
    }

    function Footer() {
        const disableSave =
            classList?.length < 1 ||
            locations?.length < 1 ||
            staff?.length < 1 ||
            services?.filter(
                (item) =>
                    item.service_type !== ServiceApiTypeEnum.APPOINTMENT
            ).length < 1 ||
            isLoading
        return (
            <div className='flex flex-col w-full px-8 mb-5 mt-auto gap-y-5'>
                <div
                    hidden={!hasOverlap()}
                    className="flex w-full bg-yellow-100 text-yellow-800 py-4 px-5 rounded-lg items-center"
                >
                    <i className='feather-alert-triangle text-xl' />
                    &nbsp;
                    <Translation
                        content_key='common.overlapping_schedule'
                    />
                </div>
                <div className="flex w-full justify-end">
                    <button
                        className={classNames(
                            'border border-gray-300 rounded-lg py-3 inline-block mr-3 px-10'
                        )}
                        onClick={() => handleModalClose()}
                    >
                        <Translation content_key="cancel" />
                    </button>
                    <button
                        disabled={disableSave}
                        className={classNames(
                            'group relative flex justify-center',
                            'py-3 px-12 border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            disableSave
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                        onClick={() => onSubmit()}
                    >
                        {isLoading && (
                            <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        )}
                        <Translation content_key="save" />
                    </button>
                </div>
            </div>
        )
    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="xl:w-wider">
                <div className="bg-white shadow-3xl w-full overflow-x-hidden overflow-y-auto sm:rounded-md mx-auto my-4 relative border-2 border-gray-100  h-4/5 flex flex-col items-stretch">
                    {getHeader()}
                    {!!error?.message.length && (
                        <GenericError
                            error_message={error.message}
                            onCloseModal={() => {
                                setError(DEFAULT_ERROR_STATE)
                            }}
                            tag={error?.tag}
                        />
                    )}
                    {classList.length >= 5 && (
                        <div className="pl-8 text-red-400 flex items-center cursor-help mt-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <Translation content_key="Cannot add more than 5 classes at a time" />
                        </div>
                    )}
                    <div style={{ overflow: 'auto' }} className="mt-5">
                        {!!classList.length && (
                            <div
                                className={classNames(
                                    'justify-start',
                                    'flex flex-col p-2 gap-2',
                                    'pl-7 pr-7'
                                )}
                            >
                                <div className="flex gap-2 px-1 h-10 items-center">
                                    <div className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-40 mr-2">
                                        <Translation
                                            render_as="span"
                                            content_key="service"
                                        />
                                    </div>
                                    <div className="font-semibold tracking-wider w-44 font-display text-gray-500 text-xs uppercase mr-2">
                                        <Translation
                                            render_as="span"
                                            content_key="location_col_header"
                                        />
                                    </div>
                                    <div className="font-semibold tracking-wider w-36 font-display text-gray-500 text-xs uppercase">
                                        <Translation
                                            render_as="span"
                                            content_key="location_space"
                                        />
                                    </div>
                                    <div className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-40 mr-2">
                                        <Translation
                                            render_as="span"
                                            content_key="instructor_col_header"
                                        />
                                    </div>
                                    <div className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-28 ">
                                        <Translation
                                            render_as="span"
                                            content_key="date_col_header"
                                        />
                                    </div>
                                    <div className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-28 pl-5">
                                        <Translation
                                            render_as="span"
                                            content_key="time_col_header"
                                        />
                                    </div>

                                    <div className="font-semibold tracking-wider font-display text-gray-500 text-xs uppercase pl-5 basis-1/5">
                                        <Translation
                                            render_as="span"
                                            content_key="recurrence_col_header"
                                        />
                                    </div>
                                </div>
                                <div className=" flex flex-col gap-2 max-h-large overflow-auto text-sm ">
                                    {classList.map((classItm, index) => {
                                        return (
                                            <React.Fragment
                                                key={classItm.id}
                                            >
                                                <ClassItem
                                                    service={getServices()}
                                                    location={
                                                        locations as IClassOption[]
                                                    }
                                                    instructor={
                                                        staff?.map(
                                                            (curr) => ({
                                                                id: curr?.id,
                                                                name: `${curr?.user?.first_name} ${curr?.user?.last_name}`,
                                                            })
                                                        ) as unknown as IClassOption[]
                                                    }
                                                    index={index}
                                                    setClasses={
                                                        setClassList
                                                    }
                                                    removeClass={
                                                        removeClass
                                                    }
                                                    translations={
                                                        translations
                                                    }
                                                    currentItem={classItm}
                                                />
                                            </React.Fragment>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className=" pr-8 pl-8 mb-5">
                        <button
                            className={classNames(
                                'flex justify-center mt-3 border-dashed border-2 border-stone-400 h-10 items-center text-stone-400 rounded w-full',
                                classList.length >= 5 ? 'hidden' : 'block'
                            )}
                            onClick={addNewClass}
                        >
                            {addIcon()} Add Class
                        </button>
                    </div>
                    <Footer />
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default ClassModal

const ClassFilters = () => {
    return null
}
const addIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
        />
    </svg>
)
